import {
  applyTenantWidgetPreset,
  buildTenantsListPath,
  DEFAULT_TENANT_FILTERS,
} from "./Tenants/tenantsList.utils";

export const buildStripePath = ({ tab = null, filter = null, focus = null } = {}) => {
  const params = new URLSearchParams();

  if (tab) {
    params.set("tab", tab);
  }

  if (filter && filter !== "all") {
    params.set("filter", filter);
  }

  if (focus) {
    params.set("focus", focus);
  }

  const query = params.toString();
  return query ? `/stripe?${query}` : "/stripe";
};

export const buildBillingPath = ({ focus = null, hash = null, tab = null } = {}) => {
  const params = new URLSearchParams();

  if (tab) {
    params.set("tab", tab);
  }

  if (focus) {
    params.set("focus", focus);
  }

  const query = params.toString();
  let path = query ? `/billing?${query}` : "/billing";

  if (hash) {
    path += hash.startsWith("#") ? hash : `#${hash}`;
  }

  return path;
};

export const NAVIGATION_CONTEXT = {
  paying_tenants: {
    title: "Paying Stripe tenants",
    description: "Tenants with an active paid subscription (not on trial).",
  },
  estimated_mrr: {
    title: "Estimated MRR by plan",
    description: "Plan assignments and estimated monthly recurring revenue from the Stripe catalog.",
  },
  collected_30d: {
    title: "Collected in the last 30 days",
    description: "Revenue from paid Stripe invoices in the last 30 days (from synced invoice records).",
  },
  collected_all_time: {
    title: "All-time collected revenue",
    description: "Total paid invoice amounts synced from Stripe into Vertex360.",
  },
  outstanding: {
    title: "Outstanding invoice balance",
    description: "Open Stripe invoices with an amount still due across all tenants.",
  },
  failed_payments: {
    title: "Failed subscription payments",
    description: "Tenants with unpaid, past due, or expired Stripe subscription status.",
  },
  on_trial: {
    title: "Tenants on trial",
    description: "Active tenants currently in a trial billing period.",
  },
  wildcard_accounts: {
    title: "Wildcard billing accounts",
    description: "Tenants with non-standard wildcard commercial arrangements.",
  },
  read_only_billing: {
    title: "Read-only billing tenants",
    description: "Tenants blocked from writes due to billing or participant limits.",
  },
  active_subscriptions: {
    title: "Paying tenants",
    description: "Tenants with active Stripe subscriptions.",
  },
  collected_last_30_days: {
    title: "Collected in the last 30 days",
    description: "Paid Stripe invoices recorded in the last 30 days.",
  },
  collected_all_time: {
    title: "All-time collected revenue",
    description: "Total collected from synced Stripe invoices.",
  },
  total_tenants: {
    title: "All tenants",
    description: "Every service provider on the platform regardless of status.",
  },
  active_tenants: {
    title: "Active tenants",
    description: "Service providers with active platform status.",
  },
  suspended: {
    title: "Suspended tenants",
    description: "Tenants whose platform access has been suspended.",
  },
  read_only: {
    title: "Read-only tenants",
    description: "Tenants in read-only mode due to billing or participant limits.",
  },
  prospects: {
    title: "Prospects",
    description: "Sales leads not yet converted to tenants.",
  },
};

export const getNavigationContext = (focus) => NAVIGATION_CONTEXT[focus] || null;

export const buildDashboardFinancialLinks = (billingStats = {}, revenue = {}) => {
  const openInvoices = Number(revenue.open_invoice_count) || 0;

  return {
    paying_tenants: buildStripePath({ tab: "customers", filter: "paying" }),
    estimated_mrr: buildStripePath({ tab: "assignments", focus: "estimated_mrr" }),
    collected_30d: buildStripePath({ tab: "sync", focus: "collected_30d" }),
    collected_all_time: buildStripePath({ tab: "sync", focus: "collected_all_time" }),
    outstanding: buildStripePath({
      tab: "customers",
      filter: openInvoices > 0 ? "outstanding" : "all",
      focus: "outstanding",
    }),
    failed_payments: buildBillingPath({
      focus: "failed_payments",
      tab: "follow-up",
      hash: "attention-failed_payments",
    }),
  };
};

export const buildDashboardPlatformLinks = ({ canViewProspects = false } = {}) => ({
  total_tenants: buildTenantsListPath(
    applyTenantWidgetPreset(DEFAULT_TENANT_FILTERS, "all"),
    { focus: "total_tenants" }
  ),
  active_tenants: buildTenantsListPath(
    { ...DEFAULT_TENANT_FILTERS },
    { focus: "active_tenants" }
  ),
  on_trial: buildTenantsListPath(
    { ...DEFAULT_TENANT_FILTERS, onTrial: true },
    { focus: "on_trial" }
  ),
  suspended: buildTenantsListPath(
    {
      ...DEFAULT_TENANT_FILTERS,
      status: ["suspended"],
    },
    { focus: "suspended" }
  ),
  read_only: buildTenantsListPath(
    { ...DEFAULT_TENANT_FILTERS, readOnly: true },
    { focus: "read_only" }
  ),
  ...(canViewProspects ? { prospects: "/prospects?focus=prospects" } : {}),
});

export const STRIPE_CUSTOMER_FILTERS = [
  { id: "all", label: "All Stripe tenants" },
  { id: "paying", label: "Paying" },
  { id: "on_trial", label: "On trial" },
  { id: "failed", label: "Failed payments" },
  { id: "outstanding", label: "Open invoices" },
  { id: "leftover_ar", label: "Active + leftover AR" },
];

export const filterStripeCustomerRows = (rows = [], filter = "all") => {
  if (!filter || filter === "all") {
    return rows;
  }

  return rows.filter((row) => {
    switch (filter) {
      case "paying":
        return row.paying;
      case "on_trial":
        return row.on_trial;
      case "failed":
        return ["unpaid", "past_due", "incomplete_expired"].includes(row.subscription_status);
      case "outstanding":
        return (row.open_invoice_count || 0) > 0 || (row.outstanding_cents || 0) > 0;
      case "leftover_ar":
        return (
          (row.paying || row.subscription_status === "past_due") &&
          ((row.open_invoice_count || 0) > 0 || (row.outstanding_cents || 0) > 0)
        );
      default:
        return true;
    }
  });
};
